/**
 * Unit data fetching and management utilities
 */
import { supabase } from '@/integrations/supabase/client';
import type { MapCoordinates, UnitFilters } from '@/types/map';

// Unit type based on our database schema
export interface PropertyUnit {
  id: string;
  unit_number: string;
  block: 'A' | 'B';
  floor: 'GF' | 'second' | 'third' | 'fourth';
  unit_type: string;
  category: '1bed' | '2bed' | 'townhouse';
  bedrooms: number;
  bathrooms: number;
  powder_rooms: number;
  study_room: boolean;
  floors_included: string[];
  area_total: number | null;
  area_internal: number | null;
  area_external_covered: number | null;
  area_external_uncovered: number | null;
  view_type: 'lagoon' | 'canal';
  availability: 'available' | 'reserved' | 'sold';
  price: number | null;
  show_price: boolean;
  is_published: boolean;
  images: string[];
  floor_plan_images: string[] | null;
  architecture_drawing_id?: string | null;
  matterport_url: string | null;
  kuula_url: string | null;
  map_element_id: string | null;
  map_coordinates: unknown;
  hubspot_deal_stage: string | null;
  parking_spaces?: number | null;
  patio?: number | null;
  // 3D Render images
  render_images?: string[] | null;
  // Rotation angle for 3D renders (0-360 degrees)
  render_rotation_angle?: number | null;
  // Video hero support (optional)
  hero_video_mp4?: string | null; // MP4 video URL for hero background
  hero_video_webm?: string | null; // WebM video URL for hero background
  hero_poster_image?: string | null; // Static image shown while video loads
  created_at: string;
  updated_at: string;
}

/**
 * Get all units with optional filters
 * Only returns published units (is_published = true) for public use
 */
export async function getAllUnits(filters?: UnitFilters) {
  let query = supabase.from('property_units').select('*').eq('is_published', true);

  if (filters?.availability) {
    query = query.eq('availability', filters.availability);
  }
  if (filters?.viewType) {
    query = query.eq('view_type', filters.viewType);
  }
  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.block) {
    query = query.eq('block', filters.block);
  }
  if (filters?.floor) {
    if (filters.floor === 'fourth') {
      // For fourth (Penthouse), use separate queries for reliability
      // Query 1: floor = 'fourth' (excluding townhouses)
      // Query 2: floor = 'third' with 'fourth' in floors_included (excluding townhouses)
      const [phPrimaryResult, ph3FResult] = await Promise.all([
        supabase.from('property_units')
          .select('*')
          .eq('floor', 'fourth')
          .neq('category', 'townhouse'),
        supabase.from('property_units')
          .select('*')
          .eq('floor', 'third')
          .neq('category', 'townhouse'),
      ]);

      // Apply other filters to both result sets
      let phPrimary = phPrimaryResult.data || [];
      let ph3F = ph3FResult.data || [];

      if (filters.availability) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.availability === filters.availability);
        ph3F = ph3F.filter((u: PropertyUnit) => u.availability === filters.availability);
      }
      if (filters.viewType) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.view_type === filters.viewType);
        ph3F = ph3F.filter((u: PropertyUnit) => u.view_type === filters.viewType);
      }
      if (filters.category) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.category === filters.category);
        ph3F = ph3F.filter((u: PropertyUnit) => u.category === filters.category);
      }
      if (filters.block) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.block === filters.block);
        ph3F = ph3F.filter((u: PropertyUnit) => u.block === filters.block);
      }

      // Filter ph3F to only include penthouse units (those with 'fourth' in floors_included)
      ph3F = ph3F.filter((u: PropertyUnit) => 
        u.floors_included && 
        Array.isArray(u.floors_included) && 
        u.floors_included.includes('fourth')
      );

      const combined = [...phPrimary, ...ph3F].sort((a, b) => a.unit_number.localeCompare(b.unit_number));
      return { data: combined as PropertyUnit[], error: phPrimaryResult.error || ph3FResult.error };
    } else {
      // Inclusive floor filter: fetch all units and filter client-side
      // to include units where floor matches OR floors_included contains the floor
      const { data: allData, error: allError } = await query.order('unit_number');
      
      if (allError) {
        return { data: [], error: allError };
      }
      
      const filtered = (allData || []).filter((unit: PropertyUnit) => {
        // Include if primary floor matches
        if (unit.floor === filters.floor) return true;
        // Include if floors_included contains the floor
        if (unit.floors_included && Array.isArray(unit.floors_included) && unit.floors_included.includes(filters.floor)) {
          return true;
        }
        return false;
      });
      
      return { data: filtered as PropertyUnit[], error: null };
    }
  }

  const { data, error } = await query.order('unit_number');

  return { 
    data: (data || []) as PropertyUnit[], 
    error 
  };
}

/**
 * Get a single unit by ID
 */
export async function getUnitById(id: string) {
  const { data, error } = await supabase.from('property_units')
    .select('*')
    .eq('id', id)
    .single();

  return { 
    data: (data || null) as PropertyUnit | null, 
    error 
  };
}

/**
 * Get a single unit by unit number
 * Only returns published units for public use
 */
export async function getUnitByNumber(unitNumber: string) {
  const { data, error } = await supabase.from('property_units')
    .select('*')
    .eq('unit_number', unitNumber)
    .eq('is_published', true)
    .single();

  return { 
    data: (data || null) as PropertyUnit | null, 
    error 
  };
}

/**
 * Get count of available units (only published)
 */
export async function getAvailableUnitsCount() {
  const { count, error } = await supabase.from('property_units')
    .select('*', { count: 'exact', head: true })
    .eq('availability', 'available')
    .eq('is_published', true);

  return { count: count || 0, error };
}

/**
 * Get units grouped by category
 */
export async function getUnitsByCategory() {
  const { data, error } = await getAllUnits();
  
  if (error || !data) {
    return { data: null, error };
  }

  const grouped = data.reduce((acc, unit) => {
    const category = unit.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(unit);
    return acc;
  }, {} as Record<string, PropertyUnit[]>);

  return { data: grouped, error: null };
}

/**
 * Get units grouped by view type
 */
export async function getUnitsByViewType() {
  const { data, error } = await getAllUnits();
  
  if (error || !data) {
    return { data: null, error };
  }

  const grouped = data.reduce((acc, unit) => {
    const viewType = unit.view_type;
    if (!acc[viewType]) {
      acc[viewType] = [];
    }
    acc[viewType].push(unit);
    return acc;
  }, {} as Record<string, PropertyUnit[]>);

  return { data: grouped, error: null };
}

/**
 * Unit Configuration interface
 */
export interface UnitConfiguration {
  id: string;
  name: string;
  total_units: number;
  block: 'A' | 'B';
  unit_type: string;
  location: 'Canal View' | 'Lagoon View';
  bedrooms: number;
  bathrooms: number;
  powder_room: number;
  study_room: number;
  floor: string;
  total_area_sq_ft: number;
  internal_area_sq_ft: number;
  external_area_covered_sq_ft: number | null;
  external_area_uncovered_sq_ft: number | null;
  price: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Map configuration floor description to unit floor enum
 * Used to match units to configurations based on floor
 */
export function mapConfigFloorToUnitFloor(floorDescription: string): 'GF' | 'second' | 'third' | 'fourth' | null {
  const normalized = floorDescription.trim();
  
  if (normalized === 'Ground Floor') {
    return 'GF';
  }
  if (normalized === 'Second Floor' || normalized === '2nd Floor') {
    return 'second';
  }
  if (normalized === 'Third Floor' || normalized === '3rd Floor') {
    return 'third';
  }
  if (normalized === '4-Storey Townhouse') {
    return 'GF'; // Townhouses use GF as primary floor
  }
  if (normalized.includes('2-Storey Penthouse') || normalized.includes('2-Storey Apartment')) {
    return 'third'; // Penthouses use third as primary floor
  }
  
  return null; // Unknown floor description
}

/**
 * Extended filters for comprehensive unit filtering
 */
export interface ExtendedUnitFilters extends UnitFilters {
  bedrooms?: number;
  bathrooms?: number;
  minPrice?: number;
  maxPrice?: number;
  minArea?: number;
  maxArea?: number;
  sortBy?: 'unit_number' | 'price' | 'area_total' | 'bedrooms';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Get all unit configurations
 */
export async function getUnitConfigurations() {
  const { data, error } = await supabase.from('property_unit_configurations')
    .select('*')
    .order('block')
    .order('unit_type');

  return {
    data: (data || []) as UnitConfiguration[],
    error,
  };
}

/**
 * Get units by configuration
 */
export async function getUnitsByConfiguration(configurationId: string) {
  // First get the configuration
  const { data: config, error: configError } = await supabase
    .from('property_unit_configurations')
    .select('*')
    .eq('id', configurationId)
    .single();

  if (configError || !config) {
    return { data: null, error: configError };
  }

  // Then get units matching the configuration
  const filters: UnitFilters = {
    block: config.block,
  };

  // Match by unit_type pattern if possible
  const { data: units, error: unitsError } = await getAllUnits(filters);

  if (unitsError) {
    return { data: null, error: unitsError };
  }

  // Filter units that match this configuration - MUST include unit_type for accurate matching
  const matchingUnits = (units || []).filter(
    (unit: PropertyUnit) =>
      unit.block === config.block &&
      unit.unit_type === config.unit_type &&
      unit.bedrooms === config.bedrooms &&
      unit.bathrooms === config.bathrooms &&
          (config.location === 'Canal View' ? unit.view_type === 'canal' : unit.view_type === 'lagoon')
  );

  return { data: matchingUnits, error: null };
}

/**
 * Get filtered units with comprehensive filtering
 * Only returns published units for public use
 */
export async function getFilteredUnits(filters?: ExtendedUnitFilters) {
  let query = supabase.from('property_units').select('*').eq('is_published', true);

  if (filters?.availability) {
    query = query.eq('availability', filters.availability);
  }
  if (filters?.viewType) {
    query = query.eq('view_type', filters.viewType);
  }
  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.block) {
    query = query.eq('block', filters.block);
  }
  if (filters?.floor) {
    if (filters.floor === 'fourth') {
      // For fourth (Penthouse), use separate queries for reliability
      // Query 1: floor = 'fourth' (excluding townhouses)
      // Query 2: floor = 'third' with 'fourth' in floors_included (excluding townhouses)
      const [phPrimaryResult, ph3FResult] = await Promise.all([
        supabase.from('property_units')
          .select('*')
          .eq('floor', 'fourth')
          .neq('category', 'townhouse'),
        supabase.from('property_units')
          .select('*')
          .eq('floor', 'third')
          .neq('category', 'townhouse'),
      ]);

      // Apply other filters to both result sets
      let phPrimary = phPrimaryResult.data || [];
      let ph3F = ph3FResult.data || [];

      if (filters.availability) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.availability === filters.availability);
        ph3F = ph3F.filter((u: PropertyUnit) => u.availability === filters.availability);
      }
      if (filters.viewType) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.view_type === filters.viewType);
        ph3F = ph3F.filter((u: PropertyUnit) => u.view_type === filters.viewType);
      }
      if (filters.category) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.category === filters.category);
        ph3F = ph3F.filter((u: PropertyUnit) => u.category === filters.category);
      }
      if (filters.block) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.block === filters.block);
        ph3F = ph3F.filter((u: PropertyUnit) => u.block === filters.block);
      }
      if (filters.bedrooms !== undefined) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.bedrooms === filters.bedrooms);
        ph3F = ph3F.filter((u: PropertyUnit) => u.bedrooms === filters.bedrooms);
      }
      if (filters.bathrooms !== undefined) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.bathrooms === filters.bathrooms);
        ph3F = ph3F.filter((u: PropertyUnit) => u.bathrooms === filters.bathrooms);
      }
      if (filters.minPrice !== undefined) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.price && u.price >= filters.minPrice!);
        ph3F = ph3F.filter((u: PropertyUnit) => u.price && u.price >= filters.minPrice!);
      }
      if (filters.maxPrice !== undefined) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.price && u.price <= filters.maxPrice!);
        ph3F = ph3F.filter((u: PropertyUnit) => u.price && u.price <= filters.maxPrice!);
      }
      if (filters.minArea !== undefined) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.area_total && u.area_total >= filters.minArea!);
        ph3F = ph3F.filter((u: PropertyUnit) => u.area_total && u.area_total >= filters.minArea!);
      }
      if (filters.maxArea !== undefined) {
        phPrimary = phPrimary.filter((u: PropertyUnit) => u.area_total && u.area_total <= filters.maxArea!);
        ph3F = ph3F.filter((u: PropertyUnit) => u.area_total && u.area_total <= filters.maxArea!);
      }

      // Filter ph3F to only include penthouse units (those with 'fourth' in floors_included)
      ph3F = ph3F.filter((u: PropertyUnit) => 
        u.floors_included && 
        Array.isArray(u.floors_included) && 
        u.floors_included.includes('fourth')
      );

      const combined = [...phPrimary, ...ph3F];
      const sortBy = filters.sortBy || 'unit_number';
      const sortOrder = filters.sortOrder || 'asc';
      combined.sort((a, b) => {
        const aValue = a[sortBy as keyof PropertyUnit];
        const bValue = b[sortBy as keyof PropertyUnit];
        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });

      return {
        data: combined as PropertyUnit[],
        error: phPrimaryResult.error || ph3FResult.error,
      };
    } else {
      query = query.eq('floor', filters.floor);
    }
  }
  if (filters?.bedrooms !== undefined) {
    query = query.eq('bedrooms', filters.bedrooms);
  }
  if (filters?.bathrooms !== undefined) {
    query = query.eq('bathrooms', filters.bathrooms);
  }
  if (filters?.minPrice !== undefined) {
    query = query.gte('price', filters.minPrice);
  }
  if (filters?.maxPrice !== undefined) {
    query = query.lte('price', filters.maxPrice);
  }
  if (filters?.minArea !== undefined) {
    query = query.gte('area_total', filters.minArea);
  }
  if (filters?.maxArea !== undefined) {
    query = query.lte('area_total', filters.maxArea);
  }

  // Apply sorting
  const sortBy = filters?.sortBy || 'unit_number';
  const sortOrder = filters?.sortOrder || 'asc';
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  const { data, error } = await query;

  return {
    data: (data || []) as PropertyUnit[],
    error,
  };
}

/**
 * Get units count with optional filters
 * Completely rewritten for accuracy and simplicity
 */
export async function getUnitsCount(filters?: UnitFilters) {
  // Special handling for Penthouse floor filter
  if (filters?.floor === 'fourth') {
    // Query 1: Units with floor = 'fourth' (excluding townhouses)
    let phPrimaryQuery = supabase.from('property_units')
      .select('id', { count: 'exact', head: true })
      .eq('floor', 'fourth')
      .neq('category', 'townhouse');
    
    // Query 2: Units with floor = 'third' that have 'fourth' in floors_included (excluding townhouses)
    // We need to fetch data and filter client-side because PostgREST array contains is complex
    let ph3FQuery = supabase.from('property_units')
      .select('id,floors_included')
      .eq('floor', 'third')
      .neq('category', 'townhouse');
    
    // Apply other filters to both queries
    if (filters?.availability) {
      phPrimaryQuery = phPrimaryQuery.eq('availability', filters.availability);
      ph3FQuery = ph3FQuery.eq('availability', filters.availability);
    }
    if (filters?.viewType) {
      phPrimaryQuery = phPrimaryQuery.eq('view_type', filters.viewType);
      ph3FQuery = ph3FQuery.eq('view_type', filters.viewType);
    }
    if (filters?.category) {
      phPrimaryQuery = phPrimaryQuery.eq('category', filters.category);
      ph3FQuery = ph3FQuery.eq('category', filters.category);
    }
    if (filters?.block) {
      phPrimaryQuery = phPrimaryQuery.eq('block', filters.block);
      ph3FQuery = ph3FQuery.eq('block', filters.block);
    }
    
    const [phPrimaryResult, ph3FResult] = await Promise.all([
      phPrimaryQuery,
      ph3FQuery,
    ]);
    
    // Count third floor units that have 'fourth' in floors_included array
    interface UnitWithFloors {
      id: string;
      floors_included?: string[] | null;
    }
    const ph3FCount = (ph3FResult.data || []).filter((unit: UnitWithFloors) => 
      unit.floors_included && Array.isArray(unit.floors_included) && unit.floors_included.includes('fourth')
    ).length;
    
    const totalCount = (phPrimaryResult.count || 0) + ph3FCount;
    return { count: totalCount, error: phPrimaryResult.error || ph3FResult.error };
  }
  
  // Standard query for all other cases
  let query = supabase.from('property_units').select('id', { count: 'exact', head: true });

  // Apply filters in consistent order
  if (filters?.availability) {
    query = query.eq('availability', filters.availability);
  }
  if (filters?.viewType) {
    query = query.eq('view_type', filters.viewType);
  }
  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.block) {
    query = query.eq('block', filters.block);
  }
  if (filters?.floor) {
    query = query.eq('floor', filters.floor);
  }

  const { count, error } = await query;

  return { count: count || 0, error };
}

/**
 * Get accurate count for a specific configuration
 * Counts actual units in database matching the configuration criteria
 * Does NOT use configuration.total_units - counts actual units instead
 */
export async function getConfigurationUnitsCount(configuration: UnitConfiguration, availability?: 'available' | 'reserved' | 'sold') {
  let query = supabase.from('property_units').select('id', { count: 'exact', head: true });

  // Match configuration criteria - exact match required
  query = query.eq('block', configuration.block);
  query = query.eq('unit_type', configuration.unit_type as any);
  query = query.eq('bedrooms', configuration.bedrooms);
  query = query.eq('bathrooms', configuration.bathrooms);
  
  // Match view type based on location
  if (configuration.location === 'Canal View') {
    query = query.eq('view_type', 'canal');
  } else if (configuration.location === 'Lagoon View') {
    query = query.eq('view_type', 'lagoon');
  }

  // Filter by availability if specified
  if (availability) {
    query = query.eq('availability', availability);
  }

  const { count, error } = await query;

  return { count: count || 0, error };
}

/**
 * Verify count accuracy by comparing count query with actual data
 * Useful for debugging count discrepancies
 */
export async function verifyCountAccuracy(filters?: UnitFilters) {
  // Get count
  const { count, error: countError } = await getUnitsCount(filters);
  
  // Get actual data with same filters
  const { data, error: dataError } = await getAllUnits(filters);
  
  const actualCount = data?.length || 0;
  const matches = count === actualCount;
  
  return {
    countQuery: count,
    actualCount,
    matches,
    discrepancy: count - actualCount,
    countError,
    dataError,
  };
}

